package co.uk.paxes.pinpoint.model

/**
 * AI player implementation using strategy pattern
 */
class AIPlayer(
    val id: String,
    val name: String,
    val difficulty: String = "EASY"
) {
    private val strategy: AIStrategy = createStrategy(difficulty)
    private val policyBoardAnalyzer = BoardAnalyzer() // Default internal analyzer
    
    /**
     * Generate a move for the AI player using the appropriate strategy
     */
    fun generateMove(gameState: GameState, availablePins: List<Pin>, boardAnalyzer: BoardAnalyzer = policyBoardAnalyzer): Move? {
        if (availablePins.isEmpty()) {
            return null
        }
        
        val emptyPositions = getEmptyPositions(gameState.board)
        if (emptyPositions.isEmpty()) {
            return null
        }
        
        val move = try {
            strategy.selectMove(gameState, availablePins, boardAnalyzer)
        } catch (e: Exception) {
            generateRandomMove(gameState, availablePins, emptyPositions)
        }
        
        // Validate the generated move
        if (move != null && gameState.board.isEmpty(move.position)) {
            return move
        } else {
            return generateRandomMove(gameState, availablePins, emptyPositions)
        }
    }
    
    /**
     * Create the appropriate strategy for the difficulty level
     */
    private fun createStrategy(difficulty: String): AIStrategy {
        return when (difficulty.uppercase()) {
            "EASY" -> EasyStrategy(id)
            "MEDIUM" -> MediumStrategy(id)
            "HARD" -> HardStrategy(id)
            "EXPERT" -> ExpertStrategy(id)
            else -> EasyStrategy(id)
        }
    }
    
    /**
     * Get all empty positions on the board
     */
    private fun getEmptyPositions(board: Board): List<Position> {
        val empty = mutableListOf<Position>()
        // Iterate through all 9 sections
        for (sectionRow in 0..2) {
            for (sectionCol in 0..2) {
                // Determine valid local coordinate ranges based on section
                val maxLocalRow = if (sectionRow == 0) 3 else 2  // Top section: 0-3, others: 0-2
                val maxLocalCol = if (sectionCol == 1) 3 else 2  // Center section: 0-3, others: 0-2
                
                // Iterate through positions within each section
                for (localRow in 0..maxLocalRow) {
                    for (localCol in 0..maxLocalCol) {
                        val pos = Position(sectionRow, sectionCol, localRow, localCol)
                        
                        if (board.isEmpty(pos)) {
                            empty.add(pos)
                        }
                    }
                }
            }
        }
        return empty
    }
    
    /**
     * Generate random move as fallback
     */
    fun generateRandomMove(
        gameState: GameState,
        availablePins: List<Pin>,
        emptyPositions: List<Position>
    ): Move {
        val randomPin = availablePins.random()
        val randomPosition = emptyPositions.random()
        return Move(id, randomPin, randomPosition)
    }
}